/** @jsxImportSource @emotion/react */
import closeBtn from '../../assets/icons/modalCloseBtn.svg';
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { Row } from '../flexbox';
import { XXXXLargeText, DROP_SHADOW } from '../../styles/styles';
import { doNothing, assoc } from '../../language/module';
import { LIGHT_GREY, BLACK, WHITE, COOL_GREY } from '../../styles/colors';
import { getResource } from '../../language/resources/getResource';


const Underline = css`
  content: "";
  background-color: #000;
  position: absolute;
  width: 100%;
  height: 4px;
  bottom: 0;
  left: 0;
  transition: all 0.3s ease-in-out 0s;
`;

const TabBase = css`
  cursor: pointer;
  position: relative;
  color: ${BLACK};
  background-color: ${COOL_GREY};
  text-decoration: none;
  padding: 5px 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 100px;
  opacity: 0.4;
  transition: all 0.3s ease-in-out 0s;

  & > div {
    height: 100%;
  }
`;


const TabHover = css`
  &:hover {
    background-color: ${LIGHT_GREY};
    opacity: 1;

    & > div > span:before {
      ${Underline};
      visibility: hidden;
      -webkit-transform: scaleX(0);
      transform: scaleX(0);
    }
  }
`;


const ActiveTab = css`
  cursor: default;
  background-color: ${WHITE};
  opacity: 1;

  & > div > span:before {
    ${Underline};
    visibility: visible;
    -webkit-transform: scaleX(1);
    transform: scaleX(1);
  }
`;


const TabWrapper = css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;


const CloseButtonSizing = css`
  height: 40px;
  width: 40px;
  padding: 8px;
  margin: 20px;
  border-radius: 50%;
  border-width: 0;
`;


const CloseButton = styled('button')`
  ${CloseButtonSizing};
  background-color: transparent;
  cursor: pointer;
  
  &:hover {
    background-color: rgba( 0, 0, 0, .06);
  }
`;


const Title = styled('span')`
  ${XXXXLargeText};
  justify-self: center;
`;


const Icon = styled('img')`
  width: 40px;
  height: 40px;
  margin-right: 20px;
`;

const modalTabHeaderRow = (rowCss? : SerializedStyles) => css`
  flex-grow: 1; 
  text-align: center; 
  ${DROP_SHADOW};
  padding-bottom: 0;
  ${rowCss};
`;

export type Tab = {
  icon?: any,
  heading: string,
};


export default class ModalTabHeader extends React.PureComponent<{
  /**
   * The definition for the tabs in the header
   */
  tabs: Tab[],
  /**
   * When the user changes tabs, what should the parent do?
   */
  onTabChange?: (newTab: number) => void;
  /**
   * Whether or not the user is allowed to dismiss this modal (default: true)
   */
  dismissable?: boolean,
  /**
 * Whether or not the user is allowed to dismiss this modal (default: true)
 */
  allowTabChange?: boolean,
  /**
   * When the user goes to dismiss this modal, what does the parent want us to do?
   */
  onDismiss?: () => void,
  /**
   * The style to apply to the modal header (image, title and close button)
   */
  rootCss?: SerializedStyles,
  resources: Record<string, any>,
}, {
  /**
   * The currently selected tab
   */
  selectedTab: number,
}> {
  public state = {
    selectedTab: 0,
  };


  private onDismissModal = () => this.props.dismissable || this.props.dismissable === undefined
                                 ? (this.props.onDismiss || doNothing)()
                                 : doNothing();


  private onTabChange = (newTab: number) => () => {
    if ((this.props.allowTabChange !== undefined && !this.props.allowTabChange) || this.state.selectedTab === newTab) {
      return;
    }

    this.setState(prev => assoc('selectedTab', newTab, prev));
    this.props.onTabChange ? this.props.onTabChange(newTab) : doNothing();
  }


  private renderHeaderTabs = (tabs: Tab[], selectedTab: number): React.ReactNode[] =>
    tabs.map((tab: Tab, index: number) => (
      <div key={tab.heading}
           onClick={this.onTabChange(index)}
           css={css`
             ${TabBase};
             ${index === selectedTab ? ActiveTab : TabHover};
      `}>
        <div css={TabWrapper}>
          { tab.icon && <Icon src={tab.icon}/> }
          <Title>{tab.heading || ''}</Title>
        </div>
      </div>
  ));


  public render = () => {
    return (
      <Row alignItems='stretch'
           justifyContent='space-around'
           rootCss={modalTabHeaderRow(this.props.rootCss)}>
        {this.renderHeaderTabs(this.props.tabs, this.state.selectedTab)}
        {
          (this.props.dismissable || this.props.dismissable === undefined)
          && <CloseButton css={css`flex-basis: 40px; margin: 10px 20px;`}
                          title={getResource(this.props.resources, 'MODAL_TITLE_HEADER_CLOSE')}
                          onClick={this.onDismissModal}><img src={closeBtn} alt='Close'/></CloseButton>
        }
      </Row>
    );
  }
}
